import { ResetPasswordDto } from 'libs/end-to-end/src/auth';
import { ResetPasswordSuccessDto } from 'libs/end-to-end/src/auth';
import { deserializeError } from 'libs/end-to-end/src/api-error';
import { isValidPasswordComplexity } from 'libs/end-to-end/src/passwords';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { AuthLayout } from 'apps/sigma2-ui/src/components/AuthLayout';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { FormValidationError } from 'apps/sigma2-ui/src/components/ui/FormValidationError';
import { TextInput } from 'apps/sigma2-ui/src/components/ui/TextInput';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { apiCall } from 'apps/sigma2-ui/src/lib/api';
import { documentTitle } from 'apps/sigma2-ui/src/lib/document-head';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { IconVisibility24pxSvg } from 'apps/sigma2-ui/src/components/svg/IconVisibility24px';
import { IconVisibilityOff24pxSvg } from 'apps/sigma2-ui/src/components/svg/IconVisibilityOff24px';

export const ResetPasswordPage: React.FC = () => {
  const { resetToken: routeResetToken } = useParams();
  const [searchParams] = useSearchParams();

  const routeEmail = searchParams.get('email');

  if (!routeResetToken) {
    throw new Error('Reset token is required');
  }

  if (!routeEmail) {
    throw new Error('Email is required');
  }

  const { myUser, myUserStatus, login } = useUser({
    onError: () => {
      setError(language.errorGeneric());
    },
  });

  const navigate = useNavigateSafe();

  // TODO: This is a duplicate of code in HomePage, let's make it a common hook if possible.
  useEffect(() => {
    if (myUserStatus === 'logged_in') {
      navigate('/overview');
    }
  }, [myUser, myUserStatus, navigate]);

  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');

  const language = useLanguage();

  const [error, setError] = useState<string | null>(null);
  const [isPasswordShown, setPasswordShown] = useState<boolean>(false);
  const [isSubmissionPending, setIsSubmissionPending] =
    useState<boolean>(false);

  const isSubmitDisabled = useMemo(() => {
    if (isSubmissionPending || !password || !confirmPassword) {
      return true;
    }

    if (
      !isValidPasswordComplexity(password) ||
      !isValidPasswordComplexity(confirmPassword)
    ) {
      return true;
    }

    if (password !== confirmPassword) {
      return true;
    }

    return false;
  }, [isSubmissionPending, password, confirmPassword]);

  const submit = useAsyncCallback(
    async (isAlive) => {
      try {
        if (!isAlive() || !password || !confirmPassword) {
          return;
        }

        if (
          !isValidPasswordComplexity(password) ||
          password !== confirmPassword
        ) {
          return;
        }

        setIsSubmissionPending(true);

        const request: ResetPasswordDto = {
          resetToken: routeResetToken,
          email: routeEmail,
          newPassword: password,
        };

        const resetResult = await apiCall({
          responseSchema: ResetPasswordSuccessDto,
          deserializeErrorFn: deserializeError,
          url: `${getApiBaseUrl()}/auth/reset-password`,
          requestOptions: {
            method: 'POST',
            body: JSON.stringify(request),
          },
        });

        if (!isAlive()) {
          return;
        }

        if (resetResult.resultType === 'err') {
          let errorMessage = language.errorGeneric();
          const error = resetResult.error;

          if (error.errorType === 'InvalidResetPasswordTokenError') {
            errorMessage = language.authErrorExpiredResetPassword();
          }

          setError(errorMessage);
          return;
        }

        const { email } = resetResult.value;

        // We reset the password, let's log them in for them :)
        login(email, password);
      } finally {
        setIsSubmissionPending(false);
      }
    },
    [password, confirmPassword, routeResetToken, routeEmail, login, language],
  );

  const clientPasswordError = useMemo(() => {
    if (password === null) {
      return null;
    }

    if (password.length <= 0) {
      return null;
    }

    if (isValidPasswordComplexity(password)) {
      return null;
    }

    return language.authErrorWeakPassword();
  }, [password, language]);

  const clientConfirmPasswordError = useMemo(() => {
    if (confirmPassword === null) {
      return null;
    }

    if (confirmPassword.length <= 0) {
      return null;
    }

    if (password !== confirmPassword) {
      return language.authErrorConfirmPasswordNotMatching();
    }

    return null;
  }, [password, confirmPassword, language]);

  const ui = useMemo(() => {
    return (
      <AuthLayout>
        <Helmet>
          <title>{documentTitle(language.createNewPassword())}</title>
        </Helmet>
        <h1
          className="-translate-y-full absolute font-normal pb-6 text-2xl text-gray-100 transform"
          data-qa="createPasswordHeader"
        >
          {language.createNewPassword()}
        </h1>
        {error !== null && (
          <FormValidationError data-qa="serverError" className="mb-5">
            <p>{error}</p>
          </FormValidationError>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          noValidate
        >
          <div className="text-left">
            <label htmlFor="password-input">
              {language.passwordInputLabel()}
            </label>
            <div className="relative">
              <TextInput
                ref={passwordRef}
                id="password-input"
                name="password"
                data-qa="passwordInput"
                onChange={(e) => setPassword(e.currentTarget.value)}
                value={password}
                type={isPasswordShown ? 'text' : 'password'}
                autoComplete="new-password"
                required
              />
              <button
                data-qa="passwordVisibilityToggle"
                type="button"
                className="text-gray-900 hover:text-tertiary w-6 absolute mt-1.5 top-0 h-12 focus-visible:outline-none focus-visible:text-tertiary"
                style={{ bottom: '1rem', right: '1rem' }}
                title={isPasswordShown ? 'hide password' : 'show password'}
                onClick={() =>
                  setPasswordShown((prevPasswordShown) => !prevPasswordShown)
                }
                tabIndex={-1}
              >
                {isPasswordShown ? (
                  <IconVisibilityOff24pxSvg />
                ) : (
                  <IconVisibility24pxSvg />
                )}
              </button>
            </div>
            {clientPasswordError && (
              <p className="mt-2" data-qa="passwordError">
                {clientPasswordError}
              </p>
            )}
          </div>
          <div className="relative mt-6 mb-1">
            <label htmlFor="confirm-password-input">
              {language.confirmPasswordInputLabel()}
            </label>
            <div className="relative">
              <TextInput
                ref={confirmPasswordRef}
                id="confirm-password-input"
                name="confirmPassword"
                data-qa="confirmPasswordInput"
                onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                value={confirmPassword}
                type="password"
                autoComplete="new-password"
                required
              />
            </div>
            {clientConfirmPasswordError && (
              <p className="mt-2" data-qa="confirmPasswordError">
                {clientConfirmPasswordError}
              </p>
            )}
          </div>
          <div className="flex justify-end items-start mt-6">
            <LegacyButton
              type="submit"
              variant={LegacyButtonVariant.PRIMARY_DARK}
              disabled={isSubmitDisabled}
            >
              {language.resetPassword()}
            </LegacyButton>
          </div>
        </form>
      </AuthLayout>
    );
  }, [
    language,
    error,
    password,
    isPasswordShown,
    clientPasswordError,
    confirmPassword,
    clientConfirmPasswordError,
    isSubmitDisabled,
    submit,
  ]);

  return ui;
};
